// Broadcast.jsx
import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/ui/page-header';
import Button from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Label, Input, Select } from '../../components/ui/field';

function Broadcast() {
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [audienceMode, setAudienceMode] = useState('all');

    // ─── Date range picker state ───
    const [dateRangeMode, setDateRangeMode] = useState('all');
    const [showDateModal, setShowDateModal] = useState(false);
    const [tempDateFrom, setTempDateFrom] = useState('');
    const [tempDateTo, setTempDateTo] = useState('');
    const [savedDateRange, setSavedDateRange] = useState({ from: '', to: '' });

    // ─── Filter form state ───
    const [filters, setFilters] = useState({
        walletBalanceMin: '',
        walletBalanceMax: '',
        status: '',
        aadharKyc: '',
        mobileVerified: '',
        emailVerified: '',
        panVerified: '',
        role: '',
        holdAmountMin: '',
        holdAmountMax: ''
    });

    // ─── Channel selection state (radio — only one) ───
    const [selectedChannel, setSelectedChannel] = useState('');

    // ─── Message content state ───
    const [messageTitle, setMessageTitle] = useState('');
    const [messageBody, setMessageBody] = useState('');
    const [pushImageUrl, setPushImageUrl] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [whatsappTemplate, setWhatsappTemplate] = useState('');

    // ─── Result / confirmation state ───
    const [userCount, setUserCount] = useState(null);
    const [loadingCount, setLoadingCount] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);

    // ─── Options ───
    const statusOptions = [
        { id: '', name: 'All Status' },
        { id: '1', name: 'Regular' },
        { id: '2', name: 'Block' },
        { id: '3', name: 'Suspended' },
        { id: '4', name: 'Deleted' },
        { id: '5', name: 'Debit Freeze' },
        { id: '6', name: 'Pending' }
    ];

    const yesNoOptions = [
        { id: '', name: 'Any' },
        { id: '1', name: 'Yes' },
        { id: '0', name: 'No' }
    ];

    const roleOptions = [
        { id: '', name: 'All Roles' },
        { id: '1', name: 'Admin' },
        { id: '2', name: 'Co-admin' },
        { id: '3', name: 'Employee' },
        { id: '4', name: 'Shareholder' },
        { id: '5', name: 'Retailer' },
        { id: '6', name: 'API' },
        { id: '7', name: 'Agent' }
    ];

    const channelOptions = [
        { key: 'pushNotification', label: 'Push Notification' },
        { key: 'email', label: 'Email' },
        { key: 'textSms', label: 'Text SMS' },
        { key: 'whatsappApi', label: 'WhatsApp API' }
    ];

    const whatsappTemplates = [
        { id: 'tmpl_001', name: 'Welcome Greeting' },
        { id: 'tmpl_002', name: 'KYC Reminder' },
        { id: 'tmpl_003', name: 'Transaction Alert' },
        { id: 'tmpl_004', name: 'Balance Low Alert' },
        { id: 'tmpl_005', name: 'Promotional Offer' }
    ];

    // ─── Variable list shown beside message fields ───
    const templateVariables = [
        { name: 'Name', syntax: '{name}' },
        { name: 'Email', syntax: '{email}' },
        { name: 'Mobile', syntax: '{mobile}' },
        { name: 'Balance', syntax: '{balance}' },
        { name: 'Hold Balance', syntax: '{hold_balance}' },
        { name: 'User ID', syntax: '{user_id}' },
        { name: 'Refer Code', syntax: '{refer_code}' },
    ];

    // ─── Handlers ───
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleChannelSelect = (key) => {
        setSelectedChannel(key);
        setMessageTitle('');
        setMessageBody('');
        setPushImageUrl('');
        setEmailSubject('');
        setEmailBody('');
        setWhatsappTemplate('');
    };

    const isValidUrl = (value) => {
        try {
            const u = new URL(value);
            return u.protocol === 'http:' || u.protocol === 'https:';
        } catch {
            return false;
        }
    };

    // ─── Date range handlers ───
    const handleDateRangeModeChange = (mode) => {
        setDateRangeMode(mode);
        if (mode === 'all') {
            setSavedDateRange({ from: '', to: '' });
        } else {
            setShowDateModal(true);
        }
    };

    const handleSaveDateRange = () => {
        if (!tempDateFrom || !tempDateTo) {
            toast.error('Please select both From and To dates');
            return;
        }
        if (tempDateFrom > tempDateTo) {
            toast.error('From date cannot be after To date');
            return;
        }
        setSavedDateRange({ from: tempDateFrom, to: tempDateTo });
        setShowDateModal(false);
    };

    const handleCloseDateModal = () => {
        setShowDateModal(false);
        if (!savedDateRange.from && !savedDateRange.to) {
            setDateRangeMode('all');
        }
        setTempDateFrom('');
        setTempDateTo('');
    };

    const buildFilterParams = () => {
        if (audienceMode === 'all') {
            return { mode: 'all' };
        }
        return {
            mode: 'filter',
            ...filters,
            dateFrom: dateRangeMode === 'custom' ? savedDateRange.from : '',
            dateTo: dateRangeMode === 'custom' ? savedDateRange.to : ''
        };
    };

    // ─── Step 1 → Step 2 ───
    const handleNextFromFilter = async () => {
        if (audienceMode === 'filter') {
            if (
                filters.walletBalanceMin !== '' &&
                filters.walletBalanceMax !== '' &&
                Number(filters.walletBalanceMin) > Number(filters.walletBalanceMax)
            ) {
                toast.error('Wallet Balance Min cannot be greater than Max');
                return;
            }
            if (
                filters.holdAmountMin !== '' &&
                filters.holdAmountMax !== '' &&
                Number(filters.holdAmountMin) > Number(filters.holdAmountMax)
            ) {
                toast.error('Hold Amount Min cannot be greater than Max');
                return;
            }
            if (dateRangeMode === 'custom' && (!savedDateRange.from || !savedDateRange.to)) {
                toast.error('Please complete the custom date range selection');
                return;
            }
        }

        setLoadingCount(true);
        setUserCount(null);
        try {
            const params = buildFilterParams();
            const result = await apiCall('push-notification-count', params, true);

            if (result.status === true) {
                const count = result.data?.count ?? result.count ?? 0;
                if (count === 0) {
                    toast.error('No users match your criteria. Please adjust your filters.');
                    return;
                }
                setUserCount(count);
                setStep(2);
            } else {
                toast.error(result.message || 'Failed to fetch users');
            }
        } catch (error) {
            toast.error('Network error. Please try again.');
        } finally {
            setLoadingCount(false);
        }
    };

    // ─── Step 2 → Step 3 ───
    const handleNextFromChannels = () => {
        if (!selectedChannel) {
            toast.error('Please select a delivery channel');
            return;
        }

        if (selectedChannel === 'pushNotification') {
            if (!messageTitle.trim()) {
                toast.error('Please enter a notification title');
                return;
            }
            if (!messageBody.trim()) {
                toast.error('Please enter a notification body');
                return;
            }
            if (pushImageUrl.trim() && !isValidUrl(pushImageUrl.trim())) {
                toast.error('Please enter a valid image URL (http:// or https://)');
                return;
            }
        }
        if (selectedChannel === 'email') {
            if (!emailSubject.trim()) {
                toast.error('Please enter an email subject');
                return;
            }
            if (!emailBody.trim()) {
                toast.error('Please enter an email body');
                return;
            }
        }
        if (selectedChannel === 'textSms') {
            if (!messageBody.trim()) {
                toast.error('Please enter an SMS body');
                return;
            }
        }
        if (selectedChannel === 'whatsappApi') {
            if (!whatsappTemplate) {
                toast.error('Please select a WhatsApp template');
                return;
            }
        }

        setStep(3);
    };

    // ─── Step 3: send ───
    const handleSend = async () => {
        setSending(true);
        try {
            const payload = {
                audienceMode,
                filters: audienceMode === 'filter' ? {
                    ...filters,
                    dateFrom: dateRangeMode === 'custom' ? savedDateRange.from : '',
                    dateTo: dateRangeMode === 'custom' ? savedDateRange.to : ''
                } : undefined,
                channel: selectedChannel,
                message: selectedChannel === 'pushNotification'
                    ? {
                        title: messageTitle,
                        body: messageBody,
                        imageUrl: pushImageUrl.trim() || null
                    }
                    : selectedChannel === 'email'
                        ? { subject: emailSubject, body: emailBody }
                        : selectedChannel === 'textSms'
                            ? { body: messageBody }
                            : { templateId: whatsappTemplate }
            };

            const result = await apiCall('push-notification-send', payload, true);

            if (result.status === true) {
                setSendResult(result.data || { success: true });
                toast.success('Notification sent successfully');
            } else {
                toast.error(result.message || 'Failed to send notification');
            }
        } catch (error) {
            toast.error('Network error. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const goBack = () => setStep(prev => Math.max(1, prev - 1));

    const resetAll = () => {
        setStep(1);
        setAudienceMode('all');
        setDateRangeMode('all');
        setSavedDateRange({ from: '', to: '' });
        setFilters({
            walletBalanceMin: '',
            walletBalanceMax: '',
            status: '',
            aadharKyc: '',
            mobileVerified: '',
            emailVerified: '',
            panVerified: '',
            role: '',
            holdAmountMin: '',
            holdAmountMax: ''
        });
        setSelectedChannel('');
        setMessageTitle('');
        setMessageBody('');
        setPushImageUrl('');
        setEmailSubject('');
        setEmailBody('');
        setWhatsappTemplate('');
        setUserCount(null);
        setSendResult(null);
    };

    const steps = [
        { number: 1, label: 'Audience' },
        { number: 2, label: 'Channel & Message' },
        { number: 3, label: 'Confirm' }
    ];

    // ═══════════════ Reusable: Variables reference panel ═══════════════
    const VariablesPanel = () => (
        <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4 lg:sticky lg:top-4 self-start">
            <div className="flex items-center gap-2 mb-3">
                <svg className="w-4 h-4 text-brand-600 dark:text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Variables
                </h4>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                Insert these placeholders in your message — they'll be replaced per recipient.
            </p>
            <div className="overflow-hidden rounded-md border border-slate-200 dark:border-slate-700">
                <table className="w-full text-xs">
                    <thead className="bg-slate-100 dark:bg-slate-700/60">
                        <tr>
                            <th className="text-left px-2.5 py-1.5 font-semibold text-slate-600 dark:text-slate-300">
                                Name
                            </th>
                            <th className="text-left px-2.5 py-1.5 font-semibold text-slate-600 dark:text-slate-300">
                                Syntax
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                        {templateVariables.map(v => (
                            <tr key={v.syntax} className="hover:bg-slate-100 dark:hover:bg-slate-700/40">
                                <td className="px-2.5 py-1.5 text-slate-700 dark:text-slate-200 whitespace-nowrap">
                                    {v.name}
                                </td>
                                <td className="px-2.5 py-1.5 whitespace-nowrap">
                                    <code className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300">
                                        {v.syntax}
                                    </code>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <>
            <PageHeader
                title="Broadcast"
                description="Send notifications to your users via multiple channels"
            >
                {step === 3 && sendResult ? (
                    <Button variant="secondary" onClick={resetAll}>
                        New Broadcast
                    </Button>
                ) : null}
            </PageHeader>

            {/* Step Indicator */}
            <div className="mb-8">
                <div className="flex items-center justify-center gap-2 sm:gap-4">
                    {steps.map((s, idx) => (
                        <React.Fragment key={s.number}>
                            <div className="flex items-center gap-2">
                                <div
                                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${step === s.number
                                        ? 'bg-brand-600 text-white'
                                        : step > s.number
                                            ? 'bg-emerald-500 text-white'
                                            : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                                        }`}
                                >
                                    {step > s.number ? '✓' : s.number}
                                </div>
                                <span
                                    className={`text-xs sm:text-sm font-medium ${step >= s.number
                                        ? 'text-slate-900 dark:text-slate-100'
                                        : 'text-slate-400 dark:text-slate-500'
                                        }`}
                                >
                                    {s.label}
                                </span>
                            </div>
                            {idx < steps.length - 1 && (
                                <div
                                    className={`w-8 sm:w-16 h-0.5 ${step > s.number
                                        ? 'bg-emerald-500'
                                        : 'bg-slate-200 dark:bg-slate-700'
                                        }`}
                                />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* ═══════════════ STEP 1: AUDIENCE ═══════════════ */}
            {step === 1 && (
                <Card className="p-6">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                        Select Audience
                    </h2>

                    <div className="flex flex-col sm:flex-row gap-4 mb-6">
                        <label
                            className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${audienceMode === 'all'
                                ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                                }`}
                        >
                            <input
                                type="radio"
                                name="audienceMode"
                                value="all"
                                checked={audienceMode === 'all'}
                                onChange={() => setAudienceMode('all')}
                                className="w-4 h-4 text-brand-600 focus:ring-brand-500"
                            />
                            <div>
                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    All Users
                                </p>
                            </div>
                        </label>

                        <label
                            className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${audienceMode === 'filter'
                                ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                                }`}
                        >
                            <input
                                type="radio"
                                name="audienceMode"
                                value="filter"
                                checked={audienceMode === 'filter'}
                                onChange={() => setAudienceMode('filter')}
                                className="w-4 h-4 text-brand-600 focus:ring-brand-500"
                            />
                            <div>
                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    Filter Users
                                </p>
                            </div>
                        </label>
                    </div>

                    {audienceMode === 'filter' && (
                        <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4">
                                Filter Criteria
                            </h3>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div>
                                    <Label>Wallet Balance (Min)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={filters.walletBalanceMin}
                                        onChange={(e) => handleFilterChange('walletBalanceMin', e.target.value)}
                                        placeholder="Min"
                                        className="w-full px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <Label>Wallet Balance (Max)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={filters.walletBalanceMax}
                                        onChange={(e) => handleFilterChange('walletBalanceMax', e.target.value)}
                                        placeholder="Max"
                                        className="w-full px-3 py-2"
                                    />
                                </div>

                                <div>
                                    <Label>Status</Label>
                                    <Select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        className="w-full px-3 py-2"
                                    >
                                        {statusOptions.map(opt => (
                                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                                        ))}
                                    </Select>
                                </div>

                                <div>
                                    <Label>Aadhar KYC</Label>
                                    <Select
                                        value={filters.aadharKyc}
                                        onChange={(e) => handleFilterChange('aadharKyc', e.target.value)}
                                        className="w-full px-3 py-2"
                                    >
                                        {yesNoOptions.map(opt => (
                                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                                        ))}
                                    </Select>
                                </div>

                                <div>
                                    <Label>Mobile Verified</Label>
                                    <Select
                                        value={filters.mobileVerified}
                                        onChange={(e) => handleFilterChange('mobileVerified', e.target.value)}
                                        className="w-full px-3 py-2"
                                    >
                                        {yesNoOptions.map(opt => (
                                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                                        ))}
                                    </Select>
                                </div>

                                <div>
                                    <Label>Email Verified</Label>
                                    <Select
                                        value={filters.emailVerified}
                                        onChange={(e) => handleFilterChange('emailVerified', e.target.value)}
                                        className="w-full px-3 py-2"
                                    >
                                        {yesNoOptions.map(opt => (
                                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                                        ))}
                                    </Select>
                                </div>

                                <div>
                                    <Label>PAN Verified</Label>
                                    <Select
                                        value={filters.panVerified}
                                        onChange={(e) => handleFilterChange('panVerified', e.target.value)}
                                        className="w-full px-3 py-2"
                                    >
                                        {yesNoOptions.map(opt => (
                                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                                        ))}
                                    </Select>
                                </div>

                                <div>
                                    <Label>Role</Label>
                                    <Select
                                        value={filters.role}
                                        onChange={(e) => handleFilterChange('role', e.target.value)}
                                        className="w-full px-3 py-2"
                                    >
                                        {roleOptions.map(opt => (
                                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                                        ))}
                                    </Select>
                                </div>

                                <div>
                                    <Label>Date Range</Label>
                                    <Select
                                        value={dateRangeMode}
                                        onChange={(e) => handleDateRangeModeChange(e.target.value)}
                                        className="w-full px-3 py-2"
                                    >
                                        <option value="all">All Time</option>
                                        <option value="custom">Custom Date</option>
                                    </Select>
                                    {dateRangeMode === 'custom' && savedDateRange.from && savedDateRange.to && (
                                        <p className="mt-2 text-xs text-brand-600 dark:text-brand-400 font-medium">
                                            📅 {savedDateRange.from} → {savedDateRange.to}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <Label>Hold Amount (Min)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={filters.holdAmountMin}
                                        onChange={(e) => handleFilterChange('holdAmountMin', e.target.value)}
                                        placeholder="Min"
                                        className="w-full px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <Label>Hold Amount (Max)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={filters.holdAmountMax}
                                        onChange={(e) => handleFilterChange('holdAmountMax', e.target.value)}
                                        placeholder="Max"
                                        className="w-full px-3 py-2"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <Button variant="secondary" onClick={() => navigate(-1)}>
                            Cancel
                        </Button>
                        <Button onClick={handleNextFromFilter} disabled={loadingCount}>
                            {loadingCount ? 'Fetching users...' : 'Next: Channel & Message →'}
                        </Button>
                    </div>
                </Card>
            )}

            {/* ═══════════════ STEP 2: CHANNEL & MESSAGE ═══════════════ */}
            {step === 2 && (
                <Card className="p-6">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                        Delivery Channel & Message
                    </h2>

                    {userCount !== null && (
                        <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                                {userCount} users will receive this message
                            </span>
                        </div>
                    )}

                    <div className="mb-6 mt-4">
                        <Label>Delivery Channel (select one)</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                            {channelOptions.map(ch => (
                                <label
                                    key={ch.key}
                                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${selectedChannel === ch.key
                                        ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                                        }`}
                                >
                                    <input
                                        type="radio"
                                        name="channel"
                                        value={ch.key}
                                        checked={selectedChannel === ch.key}
                                        onChange={() => handleChannelSelect(ch.key)}
                                        className="w-4 h-4 text-brand-600 focus:ring-brand-500"
                                    />
                                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                        {ch.label}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {selectedChannel && (
                        <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4">
                                {selectedChannel === 'pushNotification' && 'Push Notification Content'}
                                {selectedChannel === 'email' && 'Email Content'}
                                {selectedChannel === 'textSms' && 'SMS Content'}
                                {selectedChannel === 'whatsappApi' && 'WhatsApp Template'}
                            </h3>

                            {/* ═══ Two-column layout: fields (left) + variables (right) ═══ */}
                            <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">

                                {/* ─── LEFT: Channel-specific fields ─── */}
                                <div className="min-w-0">

                                    {selectedChannel === 'pushNotification' && (
                                        <>
                                            <div className="mb-4">
                                                <Label>Notification Title</Label>
                                                <Input
                                                    type="text"
                                                    value={messageTitle}
                                                    onChange={(e) => setMessageTitle(e.target.value)}
                                                    placeholder="Enter notification title..."
                                                    className="w-full px-3 py-2"
                                                />
                                            </div>
                                            <div className="mb-4">
                                                <Label>Notification Body</Label>
                                                <textarea
                                                    value={messageBody}
                                                    onChange={(e) => setMessageBody(e.target.value)}
                                                    placeholder="Write your notification message here..."
                                                    rows={4}
                                                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-y"
                                                />
                                            </div>

                                            <div>
                                                <Label>Banner / Image URL (optional)</Label>
                                                <Input
                                                    type="url"
                                                    value={pushImageUrl}
                                                    onChange={(e) => setPushImageUrl(e.target.value)}
                                                    placeholder="https://example.com/banner.jpg"
                                                    className="w-full px-3 py-2"
                                                />
                                                <p className="text-xs text-slate-400 mt-1">
                                                    Attach an image to the push notification. Leave empty for a text-only notification.
                                                </p>
                                                {pushImageUrl.trim() && isValidUrl(pushImageUrl.trim()) && (
                                                    <div className="mt-3">
                                                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                                                            Preview:
                                                        </p>
                                                        <img
                                                            src={pushImageUrl.trim()}
                                                            alt="Push notification banner preview"
                                                            className="max-h-40 rounded-lg border border-slate-200 dark:border-slate-700 object-cover"
                                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    {selectedChannel === 'email' && (
                                        <>
                                            <div className="mb-4">
                                                <Label>Email Subject</Label>
                                                <Input
                                                    type="text"
                                                    value={emailSubject}
                                                    onChange={(e) => setEmailSubject(e.target.value)}
                                                    placeholder="Enter email subject..."
                                                    className="w-full px-3 py-2"
                                                />
                                            </div>
                                            <div>
                                                <Label>Email Body</Label>
                                                <textarea
                                                    value={emailBody}
                                                    onChange={(e) => setEmailBody(e.target.value)}
                                                    placeholder="Write your email content here..."
                                                    rows={10}
                                                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-y"
                                                />
                                            </div>
                                        </>
                                    )}

                                    {selectedChannel === 'textSms' && (
                                        <div>
                                            <Label>SMS Body</Label>
                                            <textarea
                                                value={messageBody}
                                                onChange={(e) => setMessageBody(e.target.value)}
                                                placeholder="Write your SMS message here (max 160 chars)..."
                                                rows={4}
                                                maxLength={160}
                                                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-y"
                                            />
                                            <p className="text-xs text-slate-400 mt-1 text-right">
                                                {messageBody.length}/160
                                            </p>
                                        </div>
                                    )}

                                    {selectedChannel === 'whatsappApi' && (
                                        <div>
                                            <Label>WhatsApp Template</Label>
                                            <Select
                                                value={whatsappTemplate}
                                                onChange={(e) => setWhatsappTemplate(e.target.value)}
                                                className="w-full px-3 py-2"
                                            >
                                                <option value="">Select a template</option>
                                                {whatsappTemplates.map(t => (
                                                    <option key={t.id} value={t.id}>
                                                        {t.name} ({t.id})
                                                    </option>
                                                ))}
                                            </Select>
                                            <p className="text-xs text-slate-400 mt-1">
                                                The selected template will be used for all recipients.
                                            </p>
                                        </div>
                                    )}

                                </div>

                                {/* ─── RIGHT: Variables reference table ─── */}
                                <VariablesPanel />

                            </div>
                        </div>
                    )}

                    <div className="flex justify-between gap-3 mt-8 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <Button variant="secondary" onClick={goBack}>
                            ← Back
                        </Button>
                        <Button onClick={handleNextFromChannels} disabled={!selectedChannel}>
                            Next: Confirm →
                        </Button>
                    </div>
                </Card>
            )}

            {/* ═══════════════ STEP 3: CONFIRMATION ═══════════════ */}
            {step === 3 && !sendResult && (
                <Card className="p-6">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-6">
                        Confirm & Send
                    </h2>

                    <div className="bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-lg p-4 mb-6">
                        <p className="text-sm text-slate-600 dark:text-slate-300 mb-1">
                            Total users matching your criteria
                        </p>
                        <p className="text-3xl font-bold text-brand-600 dark:text-brand-400">
                            {userCount ?? '—'}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                        <div>
                            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                                Audience
                            </h3>
                            <div className="text-sm text-slate-600 dark:text-slate-300 space-y-1">
                                <p>
                                    <span className="font-medium">Mode:</span>{' '}
                                    {audienceMode === 'all' ? 'All Users' : 'Filtered Users'}
                                </p>
                                {audienceMode === 'filter' && (
                                    <>
                                        {filters.status !== '' && (
                                            <p>
                                                <span className="font-medium">Status:</span>{' '}
                                                {statusOptions.find(o => o.id === filters.status)?.name}
                                            </p>
                                        )}
                                        {filters.role !== '' && (
                                            <p>
                                                <span className="font-medium">Role:</span>{' '}
                                                {roleOptions.find(o => o.id === filters.role)?.name}
                                            </p>
                                        )}
                                        {filters.aadharKyc !== '' && (
                                            <p>
                                                <span className="font-medium">Aadhar KYC:</span>{' '}
                                                {yesNoOptions.find(o => o.id === filters.aadharKyc)?.name}
                                            </p>
                                        )}
                                        {filters.mobileVerified !== '' && (
                                            <p>
                                                <span className="font-medium">Mobile Verified:</span>{' '}
                                                {yesNoOptions.find(o => o.id === filters.mobileVerified)?.name}
                                            </p>
                                        )}
                                        {filters.emailVerified !== '' && (
                                            <p>
                                                <span className="font-medium">Email Verified:</span>{' '}
                                                {yesNoOptions.find(o => o.id === filters.emailVerified)?.name}
                                            </p>
                                        )}
                                        {filters.panVerified !== '' && (
                                            <p>
                                                <span className="font-medium">PAN Verified:</span>{' '}
                                                {yesNoOptions.find(o => o.id === filters.panVerified)?.name}
                                            </p>
                                        )}
                                        {filters.walletBalanceMin !== '' && (
                                            <p>
                                                <span className="font-medium">Wallet Balance:</span>{' '}
                                                ₹{filters.walletBalanceMin}
                                                {filters.walletBalanceMax !== '' && ` – ₹${filters.walletBalanceMax}`}
                                            </p>
                                        )}
                                        {filters.holdAmountMin !== '' && (
                                            <p>
                                                <span className="font-medium">Hold Amount:</span>{' '}
                                                ₹{filters.holdAmountMin}
                                                {filters.holdAmountMax !== '' && ` – ₹${filters.holdAmountMax}`}
                                            </p>
                                        )}
                                        <p>
                                            <span className="font-medium">Date Range:</span>{' '}
                                            {dateRangeMode === 'custom' && savedDateRange.from
                                                ? `${savedDateRange.from} to ${savedDateRange.to}`
                                                : 'All Time'}
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                                Channel
                            </h3>
                            <div className="flex flex-wrap gap-2 mb-4">
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                                    {channelOptions.find(c => c.key === selectedChannel)?.label}
                                </span>
                            </div>

                            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                                Message Preview
                            </h3>
                            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 text-sm">
                                {selectedChannel === 'pushNotification' && (
                                    <>
                                        <p className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
                                            {messageTitle || '(No title)'}
                                        </p>
                                        <p className="text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
                                            {messageBody || '(No body)'}
                                        </p>
                                        {pushImageUrl.trim() && (
                                            <div className="mt-2">
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                                                    Banner image:
                                                </p>
                                                <img
                                                    src={pushImageUrl.trim()}
                                                    alt="Banner preview"
                                                    className="max-h-32 rounded border border-slate-200 dark:border-slate-700 object-cover"
                                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                />
                                                <p className="text-xs text-slate-400 mt-1 break-all">
                                                    {pushImageUrl.trim()}
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                                {selectedChannel === 'email' && (
                                    <>
                                        <p className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
                                            Subject: {emailSubject || '(No subject)'}
                                        </p>
                                        <p className="text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
                                            {emailBody || '(No body)'}
                                        </p>
                                    </>
                                )}
                                {selectedChannel === 'textSms' && (
                                    <p className="text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
                                        {messageBody || '(No SMS body)'}
                                    </p>
                                )}
                                {selectedChannel === 'whatsappApi' && (
                                    <p className="text-slate-600 dark:text-slate-300">
                                        Template:{' '}
                                        <span className="font-semibold text-slate-900 dark:text-slate-100">
                                            {whatsappTemplates.find(t => t.id === whatsappTemplate)?.name || '(Not selected)'}
                                        </span>
                                        {' '}({whatsappTemplate})
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-between gap-3 mt-8 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <Button variant="secondary" onClick={goBack}>
                            ← Back
                        </Button>
                        <Button onClick={handleSend} disabled={sending || !userCount}>
                            {sending ? 'Sending...' : `Send to ${userCount ?? 0} users`}
                        </Button>
                    </div>
                </Card>
            )}

            {/* ═══════════════ SEND SUCCESS ═══════════════ */}
            {step === 3 && sendResult && (
                <Card className="p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                        <svg className="w-8 h-8 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                        Notification Sent Successfully
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">
                        Your {channelOptions.find(c => c.key === selectedChannel)?.label} has been queued and will be delivered to {userCount ?? 0} users.
                    </p>
                    <div className="flex justify-center gap-3">
                        <Button variant="secondary" onClick={resetAll}>
                            Send Another
                        </Button>
                        <Button onClick={() => navigate('/dashboard')}>
                            Go to Dashboard
                        </Button>
                    </div>
                </Card>
            )}

            {/* ═══════════════ DATE RANGE MODAL ═══════════════ */}
            {showDateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={handleCloseDateModal}
                    />
                    <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                            Select Custom Date Range
                        </h3>

                        <div className="space-y-4">
                            <div>
                                <Label>From Date</Label>
                                <Input
                                    type="date"
                                    value={tempDateFrom}
                                    onChange={(e) => setTempDateFrom(e.target.value)}
                                    className="w-full px-3 py-2"
                                />
                            </div>
                            <div>
                                <Label>To Date</Label>
                                <Input
                                    type="date"
                                    value={tempDateTo}
                                    onChange={(e) => setTempDateTo(e.target.value)}
                                    className="w-full px-3 py-2"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <Button variant="secondary" onClick={handleCloseDateModal}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSaveDateRange}
                                disabled={!tempDateFrom || !tempDateTo}
                            >
                                Save Date Range
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Broadcast;